/**
 * JSON-LD structured data builders (Milestone 14).
 */
import { AUTOMAPA_DEFAULT_DESCRIPTION, AUTOMAPA_LOGO_URL, AUTOMAPA_SITE_NAME, PUBLIC_SEARCH_PATH } from "./seoConstants";
import { buildAbsoluteUrl, getPublicVehicleDetailPath, resolveOgImageUrl } from "./canonicalUrls";
import { sanitizeSeoText } from "./seoUtils";
import { formatVehicleTitle } from "../utils/automapListing/listingFormatters";

export function buildBreadcrumbJsonLd(items = []) {
  const valid = (items || [])
    .filter((item) => item?.label)
    .map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: sanitizeSeoText(item.label),
      ...(item.href ? { item: buildAbsoluteUrl(item.href) } : {}),
    }));

  if (!valid.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: valid,
  };
}

export function buildOrganizationJsonLd({ sameAs = [] } = {}) {
  const url = buildAbsoluteUrl("/");
  const logo = buildAbsoluteUrl(AUTOMAPA_LOGO_URL);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: AUTOMAPA_SITE_NAME,
    url,
  };

  if (logo) schema.logo = logo;

  const social = (sameAs || []).filter((entry) => typeof entry === "string" && /^https?:\/\//.test(entry));
  if (social.length) schema.sameAs = social;

  return schema;
}

export function buildWebsiteJsonLd() {
  const url = buildAbsoluteUrl("/");
  const searchTarget = buildAbsoluteUrl(`${PUBLIC_SEARCH_PATH}?q={search_term_string}`);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: AUTOMAPA_SITE_NAME,
    url,
  };

  if (searchTarget) {
    schema.potentialAction = {
      "@type": "SearchAction",
      target: searchTarget,
      "query-input": "required name=search_term_string",
    };
  }

  return schema;
}

export function buildGlobalStructuredData(options = {}) {
  return [buildOrganizationJsonLd(options), buildWebsiteJsonLd()].filter(Boolean);
}

function mapAvailability(publicStatus) {
  if (publicStatus === "unavailable" || publicStatus === "not_found") {
    return "https://schema.org/OutOfStock";
  }
  return "https://schema.org/InStock";
}

export function buildVehicleJsonLd(listing, dealer = null, { publicStatus = "visible" } = {}) {
  if (!listing?.id) return null;

  const canonicalPath = getPublicVehicleDetailPath(listing);
  const canonicalUrl = buildAbsoluteUrl(canonicalPath);
  const name = sanitizeSeoText(formatVehicleTitle(listing, { fallback: "" }));

  if (!name) return null;

  const images = (listing.images?.length ? listing.images : [listing.primaryImageUrl])
    .filter(Boolean)
    .map((url) => resolveOgImageUrl(url))
    .filter((url) => url && !url.includes("vehicle-placeholder"));

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description: sanitizeSeoText(
      listing.seoDescription ||
        `Revisa este ${formatVehicleTitle(listing, { fallback: "vehículo usado" })} en AutoMapa.`
    ),
    url: canonicalUrl,
  };

  if (listing.make) schema.brand = sanitizeSeoText(listing.make);
  if (listing.model) schema.model = sanitizeSeoText(listing.model);
  if (images.length) schema.image = images.length === 1 ? images[0] : images;

  const priceCop = listing.priceCop ?? listing.currentPriceCop;
  const offer = {
    "@type": "Offer",
    priceCurrency: "COP",
    availability: mapAvailability(publicStatus),
    url: canonicalUrl,
  };

  if (priceCop != null && Number.isFinite(Number(priceCop))) {
    offer.price = Number(priceCop);
  }

  const dealerName = dealer?.businessName || listing.dealerName;
  if (dealerName) {
    offer.seller = {
      "@type": "AutoDealer",
      name: sanitizeSeoText(dealerName),
    };
  }

  schema.offers = offer;
  return schema;
}

export function buildDealerJsonLd(dealer, profilePath) {
  if (!dealer?.businessName) return null;

  const url = buildAbsoluteUrl(profilePath);
  const image = resolveOgImageUrl(dealer.coverImageUrl || dealer.logoUrl);

  const schema = {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    name: sanitizeSeoText(dealer.businessName),
    url,
  };

  if (image) schema.image = image;

  if (dealer.address || dealer.city) {
    schema.address = {
      "@type": "PostalAddress",
      ...(dealer.address ? { streetAddress: sanitizeSeoText(dealer.address) } : {}),
      ...(dealer.city ? { addressLocality: sanitizeSeoText(dealer.city) } : {}),
      addressCountry: "CO",
    };
  }

  if (dealer.mainWhatsapp) schema.telephone = dealer.mainWhatsapp;

  return schema;
}

export function combineStructuredData(...entries) {
  const flat = entries.flat().filter(Boolean);
  if (!flat.length) return null;
  return flat.length === 1 ? flat[0] : flat;
}
